/**
 * Генератор партнёрских ссылок Booking.com через Travelpayouts
 *
 * Формат редиректа:
 * https://tp.media/r?marker=633678&p=2076&u=<ENCODED_BOOKING_URL>&sub_id=<метка>
 *
 * Внутри u – обычный Booking URL:
 * https://www.booking.com/searchresults.html?ss=<City,Country>&lang=<locale>&selected_currency=<currency>
 */

import { wrapAffiliateLink } from '../proxy';

/**
 * Параметры Travelpayouts для Booking.com
 */
const TRAVELPAYOUTS_CONFIG = {
    baseUrl: 'https://tp.media/r',
    marker: '633678',
    programId: '2076' // p=2076 для Booking.com
};

/**
 * Резолвер валют для Booking.com
 * Логика: EUR если в стране евро, USD для всех остальных
 * 
 * @param {string} countryCurrency - валюта страны из Sanity (country.currency)
 * @returns {string} код валюты для Booking (EUR или USD)
 */
export function resolveBookingCurrency(countryCurrency) {
    // Если в стране евро – используем EUR, иначе USD
    if (countryCurrency === 'EUR') {
        return 'EUR';
    }
    return 'USD';
}

/**
 * Маппинг языков сайта в локали Booking.com
 * @param {string} siteLang - язык сайта (ru, en)
 * @returns {string} локаль для Booking.com
 */
export function mapSiteLangToBookingLocale(siteLang) {
    const localeMap = {
        'ru': 'ru-RU',
        'en': 'en-GB'
    };

    return localeMap[siteLang] || 'en-GB';
}

/**
 * Основная функция генерации партнёрской ссылки Booking.com
 * 
 * @param {Object} params - параметры для генерации ссылки
 * @param {string} params.q - строка поиска (например, "Zurich, Switzerland")
 * @param {string} params.locale - локаль ('ru-RU' или 'en-GB')
 * @param {string} params.currency - валюта ('EUR', 'CHF', 'USD')
 * @param {string} [params.checkin] - дата заезда YYYY-MM-DD (опционально)
 * @param {string} [params.checkout] - дата выезда YYYY-MM-DD (опционально)
 * @param {number} [params.adults=2] - количество взрослых
 * @param {number} [params.childrenCount=0] - количество детей (переименовано чтобы не конфликтовать с React children)
 * @param {number} [params.rooms=1] - количество комнат
 * @param {string} params.subId - идентификатор для отслеживания (например, "city-zurich")
 * @returns {string} финальная партнёрская ссылка через Travelpayouts
 */
export function buildBookingLink({
    q,
    locale,
    currency,
    checkin,
    checkout,
    adults = 2,
    childrenCount = 0,
    rooms = 1,
    subId
}) {
    // Шаг 1: Собираем целевой URL Booking.com
    const bookingUrl = new URL('https://www.booking.com/searchresults.html');
    const params = bookingUrl.searchParams;

    // Обязательные параметры
    params.set('ss', q);
    params.set('lang', locale);
    params.set('selected_currency', currency);

    // Опциональные параметры (даты/гости)
    if (checkin) params.set('checkin', checkin);
    if (checkout) params.set('checkout', checkout);

    // Гарантируем что это числа
    params.set('group_adults', String(Number(adults) || 2));
    params.set('group_children', String(Number(childrenCount) || 0));
    params.set('no_rooms', String(Number(rooms) || 1));

    // Шаг 2: Кодируем URL целиком ОДИН раз
    const encodedBookingUrl = encodeURIComponent(bookingUrl.toString());

    // Шаг 3: Собираем финальный редирект через Travelpayouts
    // ВАЖНО: НЕ используем URLSearchParams для u, чтобы избежать двойного кодирования
    // Собираем строку вручную
    let finalUrl = `${TRAVELPAYOUTS_CONFIG.baseUrl}?marker=${TRAVELPAYOUTS_CONFIG.marker}&p=${TRAVELPAYOUTS_CONFIG.programId}&u=${encodedBookingUrl}`;

    if (subId) {
        finalUrl += `&sub_id=${encodeURIComponent(subId)}`;
    }

    return wrapAffiliateLink(finalUrl);
}

/**
 * Удобная функция для генерации ссылки с автоматическим резолвингом параметров
 * 
 * @param {Object} params - параметры
 * @param {string} params.cityName - название города (латиница, например "Zurich")
 * @param {string} params.countryName - название страны (латиница, например "Switzerland")
 * @param {string} params.countryCurrency - валюта страны из Sanity (country.currency)
 * @param {string} params.citySlug - слаг города для subId
 * @param {string} params.siteLang - язык сайта ('ru' или 'en')
 * @param {string} [params.checkin] - дата заезда YYYY-MM-DD
 * @param {string} [params.checkout] - дата выезда YYYY-MM-DD
 * @param {number} [params.adults] - количество взрослых
 * @param {number} [params.childrenCount] - количество детей
 * @param {number} [params.rooms] - количество комнат
 * @param {string} [params.subIdSuffix] - суффикс для subId (по умолчанию 'hero')
 * @returns {string} финальная партнёрская ссылка
 */
export function buildBookingLinkFromData({
    cityName,
    countryName,
    countryCurrency,
    citySlug,
    siteLang,
    checkin,
    checkout,
    adults,
    childrenCount,
    rooms,
    subIdSuffix = 'hero'
}) {
    // Проверяем что есть название города
    if (!cityName) {
        return '#';
    }

    // Формируем строку поиска: "City, Country" для дизамбигуации
    const searchQuery = countryName
        ? `${cityName}, ${countryName}`
        : cityName;

    // Резолвим валюту: EUR если в стране евро, иначе USD
    const currency = resolveBookingCurrency(countryCurrency);

    // Резолвим локаль
    const locale = mapSiteLangToBookingLocale(siteLang);

    // Формируем subId
    const subId = `city-${citySlug}-${subIdSuffix}`;

    return buildBookingLink({
        q: searchQuery,
        locale,
        currency,
        checkin,
        checkout,
        adults,
        childrenCount,
        rooms,
        subId
    });
}

/**
 * Функция для генерации ссылки по стране (без конкретного города)
 * 
 * @param {Object} params - параметры
 * @param {string} params.countryName - название страны (латиница)
 * @param {string} params.countrySlug - слаг страны для subId
 * @param {string} params.countryCurrency - валюта страны из Sanity
 * @param {string} params.siteLang - язык сайта
 * @param {string} [params.subIdSuffix] - суффикс для subId
 * @returns {string} финальная партнёрская ссылка
 */
export function buildBookingCountryLink({
    countryName,
    countrySlug,
    countryCurrency,
    siteLang,
    subIdSuffix = 'country-hero'
}) {
    const currency = resolveBookingCurrency(countryCurrency);
    const locale = mapSiteLangToBookingLocale(siteLang);
    const subId = `${countrySlug}-${subIdSuffix}`;

    return buildBookingLink({
        q: countryName,
        locale,
        currency,
        subId
    });
}

// trackBookingClick удалён 2026-08-11 (аудит §4.5): дублировал affiliate_click
// из AffiliateLink под вторым именем события и дробил воронку жилья в GA4.

